import React, { useState, useEffect, useCallback, useRef, createContext, useContext } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Avatar from '@material-ui/core/Avatar';

/* ═══════════════════════════════════════════════════════
   TOAST NOTIFICATION SYSTEM — 21st.dev style
   Provides a context + provider for app-wide toast popups.

   Usage:
     import { useToast } from 'components/shared/ToastNotification';
     const toast = useToast();
     toast.show({ title: '...', desc: '...', type: 'message' });
   ═══════════════════════════════════════════════════════ */

const ToastContext = createContext(null);
export const useToast = () => useContext(ToastContext);

let toastIdCounter = 0;

const TYPE_CONFIG = {
    message: { icon: '\uD83D\uDCAC', color: '#60a5fa', label: 'New Message' },
    deal:    { icon: '\u2728', color: '#edd714', label: 'New Deal' },
    like:    { icon: '\u2764\uFE0F', color: '#ef4444', label: 'Liked' },
    follow:  { icon: '\uD83D\uDC64', color: '#22c55e', label: 'New Follow' },
    system:  { icon: '\u2699\uFE0F', color: '#a78bfa', label: 'System' },
    success: { icon: '\u2705', color: '#22c55e', label: 'Success' },
    warning: { icon: '\u26A0\uFE0F', color: '#f59e0b', label: 'Warning' },
    error:   { icon: '\u274C', color: '#ef4444', label: 'Error' },
    info:    { icon: '\u2139\uFE0F', color: '#3b82f6', label: 'Info' },
};

const useStyles = makeStyles(() => ({
    /* ── Toast container (top-right stack) ── */
    container: {
        position: 'fixed',
        top: 16,
        right: 16,
        zIndex: 10000,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        pointerEvents: 'none',
        maxWidth: 400,
        width: '100%',
    },

    /* ── Individual toast ── */
    '@keyframes toastSlideIn': {
        from: { opacity: 0, transform: 'translateX(100%) scale(0.95)' },
        to: { opacity: 1, transform: 'translateX(0) scale(1)' },
    },
    '@keyframes toastSlideOut': {
        from: { opacity: 1, transform: 'translateX(0) scale(1)' },
        to: { opacity: 0, transform: 'translateX(100%) scale(0.95)' },
    },
    toast: {
        pointerEvents: 'auto',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        padding: '14px 16px',
        borderRadius: 16,
        background: 'rgba(20, 20, 28, 0.92)',
        backdropFilter: 'blur(24px) saturate(1.4)',
        WebkitBackdropFilter: 'blur(24px) saturate(1.4)',
        border: '1px solid rgba(255,255,255,0.06)',
        boxShadow: '0 16px 48px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.02)',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        animation: '$toastSlideIn 0.35s cubic-bezier(0.16, 1, 0.3, 1)',
        '&:hover': {
            background: 'rgba(25, 25, 35, 0.96)',
            transform: 'translateX(-2px)',
            boxShadow: '0 20px 56px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.04)',
        },
    },
    toastExiting: {
        animation: '$toastSlideOut 0.3s ease forwards',
    },

    /* ── Icon bubble ── */
    iconBubble: {
        width: 38,
        height: 38,
        borderRadius: 12,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 18,
        flexShrink: 0,
        border: '1px solid rgba(255,255,255,0.04)',
    },
    toastAvatar: {
        width: 38,
        height: 38,
        fontSize: 13,
        fontWeight: 800,
        fontFamily: "'Exo 2', sans-serif",
        flexShrink: 0,
    },

    /* ── Content ── */
    content: {
        flex: 1,
        minWidth: 0,
    },
    titleRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 8,
        marginBottom: 2,
    },
    title: {
        fontFamily: "'Exo 2', sans-serif",
        fontWeight: 700,
        fontSize: 13,
        color: '#f0f0f5',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    timeLabel: {
        fontFamily: "'Exo 2', sans-serif",
        fontSize: 10,
        color: 'rgba(255,255,255,0.3)',
        whiteSpace: 'nowrap',
        flexShrink: 0,
    },
    desc: {
        fontFamily: "'Exo 2', sans-serif",
        fontSize: 12,
        lineHeight: 1.45,
        color: 'rgba(255,255,255,0.5)',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
    },

    /* ── Action buttons ── */
    actions: {
        display: 'flex',
        gap: 6,
        marginTop: 8,
    },
    actionBtn: {
        padding: '4px 12px',
        borderRadius: 8,
        fontFamily: "'Exo 2', sans-serif",
        fontSize: 11,
        fontWeight: 700,
        border: 'none',
        cursor: 'pointer',
        transition: 'all 0.15s ease',
    },
    actionPrimary: {
        background: 'rgba(237,215,20,0.1)',
        color: '#edd714',
        border: '1px solid rgba(237,215,20,0.15)',
        '&:hover': {
            background: 'rgba(237,215,20,0.2)',
        },
    },
    actionSecondary: {
        background: 'rgba(255,255,255,0.04)',
        color: 'rgba(255,255,255,0.5)',
        border: '1px solid rgba(255,255,255,0.06)',
        '&:hover': {
            background: 'rgba(255,255,255,0.08)',
            color: 'rgba(255,255,255,0.7)',
        },
    },

    /* ── Progress bar ── */
    progressTrack: {
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        height: 2,
        borderRadius: '0 0 16px 16px',
        overflow: 'hidden',
    },
    progressFill: {
        height: '100%',
        transition: 'width 0.1s linear',
    },

    /* ── Close X ── */
    closeX: {
        position: 'absolute',
        top: 6,
        right: 6,
        width: 18,
        height: 18,
        borderRadius: '50%',
        background: 'rgba(255,255,255,0.04)',
        border: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        opacity: 0,
        transition: 'opacity 0.15s ease',
        '$toast:hover &': { opacity: 0.6 },
        '&:hover': { opacity: '1 !important', background: 'rgba(255,255,255,0.1)' },
    },
}));

/* ── Single Toast Component ── */
function Toast({ toast, onDismiss, classes }) {
    const [exiting, setExiting] = useState(false);
    const [progress, setProgress] = useState(1);
    const timerRef = useRef(null);
    const startRef = useRef(Date.now());
    const pausedRef = useRef(false);
    const remainingRef = useRef(toast.duration || 5000);

    const typeConf = TYPE_CONFIG[toast.type] || TYPE_CONFIG.info;

    const dismiss = useCallback(() => {
        setExiting(true);
        setTimeout(() => onDismiss(toast.id), 300);
    }, [onDismiss, toast.id]);

    /* ── Auto-dismiss timer ── */
    useEffect(() => {
        if (toast.persistent) return;

        const duration = toast.duration || 5000;
        startRef.current = Date.now();
        remainingRef.current = duration;

        const tick = () => {
            if (pausedRef.current) {
                timerRef.current = requestAnimationFrame(tick);
                return;
            }
            const elapsed = Date.now() - startRef.current;
            const pct = Math.max(0, 1 - elapsed / remainingRef.current);
            setProgress(pct);
            if (pct <= 0) {
                dismiss();
            } else {
                timerRef.current = requestAnimationFrame(tick);
            }
        };

        timerRef.current = requestAnimationFrame(tick);
        return () => { if (timerRef.current) cancelAnimationFrame(timerRef.current); };
    }, [toast.persistent, toast.duration, dismiss]);

    const handlePause = () => {
        pausedRef.current = true;
        remainingRef.current = remainingRef.current - (Date.now() - startRef.current);
    };

    const handleResume = () => {
        pausedRef.current = false;
        startRef.current = Date.now();
    };

    return (
        <div
            className={`${classes.toast} ${exiting ? classes.toastExiting : ''}`}
            style={{ position: 'relative' }}
            onClick={() => { if (toast.onClick) toast.onClick(); dismiss(); }}
            onMouseEnter={handlePause}
            onMouseLeave={handleResume}
        >
            {/* Close button */}
            <button className={classes.closeX} onClick={(e) => { e.stopPropagation(); dismiss(); }}>
                <svg width="8" height="8" viewBox="0 0 10 10" stroke="rgba(255,255,255,0.6)" strokeWidth="1.5" strokeLinecap="round">
                    <line x1="1" y1="1" x2="9" y2="9" /><line x1="9" y1="1" x2="1" y2="9" />
                </svg>
            </button>

            {/* Icon / Avatar */}
            {toast.avatar ? (
                <Avatar
                    className={classes.toastAvatar}
                    src={toast.avatar}
                    style={{
                        background: `linear-gradient(135deg, ${typeConf.color}22, ${typeConf.color}11)`,
                        color: typeConf.color,
                        border: `2px solid ${typeConf.color}25`,
                    }}
                >
                    {toast.initials || ''}
                </Avatar>
            ) : (
                <div
                    className={classes.iconBubble}
                    style={{ background: `${typeConf.color}10`, color: typeConf.color }}
                >
                    {typeConf.icon}
                </div>
            )}

            {/* Content */}
            <div className={classes.content}>
                <div className={classes.titleRow}>
                    <span className={classes.title}>{toast.title}</span>
                    <span className={classes.timeLabel}>now</span>
                </div>
                {toast.desc && <div className={classes.desc}>{toast.desc}</div>}

                {/* Action buttons */}
                {toast.actions && toast.actions.length > 0 && (
                    <div className={classes.actions}>
                        {toast.actions.map((action, idx) => (
                            <button
                                key={idx}
                                className={`${classes.actionBtn} ${idx === 0 ? classes.actionPrimary : classes.actionSecondary}`}
                                onClick={(e) => { e.stopPropagation(); action.onClick(); dismiss(); }}
                            >
                                {action.label}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {/* Progress bar */}
            {!toast.persistent && (
                <div className={classes.progressTrack}>
                    <div
                        className={classes.progressFill}
                        style={{ width: `${progress * 100}%`, background: typeConf.color }}
                    />
                </div>
            )}
        </div>
    );
}

/* ═══════════════════════════════════════════════════════
   TOAST PROVIDER — wraps your app, provides useToast()
   ═══════════════════════════════════════════════════════ */
export function ToastProvider({ children }) {
    const classes = useStyles();
    const [toasts, setToasts] = useState([]);

    const show = useCallback((config) => {
        const id = ++toastIdCounter;
        const newToast = { id, ...config };
        setToasts(prev => [newToast, ...prev].slice(0, 5)); // max 5 visible
        return id;
    }, []);

    const dismiss = useCallback((id) => {
        setToasts(prev => prev.filter(t => t.id !== id));
    }, []);

    const dismissAll = useCallback(() => {
        setToasts([]);
    }, []);

    return (
        <ToastContext.Provider value={{ show, dismiss, dismissAll }}>
            {children}
            <div className={classes.container}>
                {toasts.map(toast => (
                    <Toast key={toast.id} toast={toast} onDismiss={dismiss} classes={classes} />
                ))}
            </div>
        </ToastContext.Provider>
    );
}

export default ToastProvider;
